import { useState } from "react";
import { Link } from "react-router-dom";
import {
  CheckCircle2,
  ChevronDown,
  ChevronUp,
  Eye,
  FileText,
  Loader2,
  XCircle,
} from "lucide-react";

import { useAuth } from "#/context/AuthProvider";
import { useConfirm } from "#/components/common/ConfirmProvider";
import { useToast } from "#/components/common/ToastProvider";
import { useApproveClaim, useClaim, useClaimsForApproval, useRejectClaim } from "../hooks";
import { ClaimStatusBadge } from "../components/ClaimStatusBadge";
import { ClaimDocumentViewer } from "../components/ClaimDocumentViewer";
import type { ClaimType, ExpenseClaimSummary } from "../types";

// ─── helpers ──────────────────────────────────────────────────────────────────

function fmt(n: number) { return `KES ${n.toLocaleString()}`; }

function fmtDate(s?: string | null) {
  if (!s) return "—";
  try { return new Date(s).toLocaleDateString(undefined, { day: "numeric", month: "short", year: "numeric" }); }
  catch { return s; }
}

function typeLabel(t: ClaimType) {
  return t.replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase());
}

const PAGE_SIZE = 10;

// ─── page ─────────────────────────────────────────────────────────────────────

export default function ClaimsApprovalPage() {

  const { hasRole, hasAnyRole, hasPermission } = useAuth();

  const isAdmin    = hasRole("SUPER_ADMIN") || hasAnyRole(["ADMIN"]);
  const canApprove = isAdmin || hasPermission("HR_CLAIM_APPROVE")
                      || hasPermission("HR_CLAIM_MANAGE") || hasPermission("FINANCE_CLAIM_APPROVE");

  const [page, setPage]             = useState(0);
  const [expandedId, setExpandedId] = useState<number | null>(null);

  const { data, isLoading, error } = useClaimsForApproval({ page, size: PAGE_SIZE });
  const claims     = data?.content ?? [];
  const total      = data?.totalElements ?? 0;
  const totalPages = data?.totalPages ?? 0;

  if (!canApprove) {
    return (
      <div className="rounded-2xl border border-red-100 bg-red-50 px-5 py-8 text-center">
        <p className="font-bold text-red-700">Access denied.</p>
        <p className="mt-1 text-sm text-red-500">You do not have permission to approve claims.</p>
        <Link to="/claims" className="mt-3 inline-block text-xs font-bold text-red-600 hover:underline">
          ← Back to my claims
        </Link>
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-4">

      {/* ── Header ── */}
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="text-xl font-black text-slate-900">Claims Approval Inbox</h1>
          {total > 0 && (
            <p className="mt-0.5 text-xs text-slate-500">
              {total} claim{total !== 1 ? "s" : ""} awaiting your decision.
            </p>
          )}
        </div>
        <Link to="/claims" className="rounded-xl border px-4 py-2 text-sm font-bold text-slate-600 hover:bg-slate-50">
          ← Dashboard
        </Link>
      </div>

      {/* ── Inbox ── */}
      {isLoading ? (
        <div className="flex items-center gap-2 rounded-2xl border bg-white px-5 py-8 text-slate-400 shadow-sm">
          <Loader2 className="h-4 w-4 animate-spin" /> Loading…
        </div>
      ) : error ? (
        <div className="rounded-2xl border border-red-100 bg-red-50 px-5 py-4 text-sm font-semibold text-red-700">
          Failed to load approval inbox —{" "}
          {(error as { response?: { status?: number } })?.response?.status ?? "network error"}.
          Check the server logs for details.
        </div>
      ) : claims.length === 0 ? (
        <div className="flex flex-col items-center gap-3 rounded-2xl border bg-white py-12 shadow-sm">
          <CheckCircle2 className="h-10 w-10 text-emerald-200" />
          <p className="text-sm text-slate-400">All caught up — no claims awaiting approval.</p>
        </div>
      ) : (
        <div className="overflow-hidden rounded-2xl border bg-white shadow-sm">
          {/* column header */}
          <div className="grid grid-cols-[auto_1fr_auto_auto] items-center gap-3 border-b bg-slate-50 px-4 py-2 text-[11px] font-bold uppercase tracking-wide text-slate-400">
            <span className="w-8" />
            <span>Employee / Claim</span>
            <span className="text-right">Amount</span>
            <span className="w-36 text-right">Actions</span>
          </div>

          <div className="divide-y">
            {claims.map((c) => (
              <ApprovalItem
                key={c.id}
                claim={c}
                expanded={expandedId === c.id}
                onToggle={() => setExpandedId(expandedId === c.id ? null : c.id)}
                onDecision={() => setExpandedId(null)}
              />
            ))}
          </div>

          <div className="flex items-center justify-between border-t bg-slate-50 px-5 py-3">
            <span className="text-xs text-slate-400">{total} claim{total !== 1 ? "s" : ""}</span>
            <div className="flex items-center gap-2">
              <button
                disabled={page === 0}
                onClick={() => setPage((p) => p - 1)}
                className="rounded-lg border px-3 py-1.5 text-xs font-bold text-slate-600 hover:bg-slate-100 disabled:opacity-40"
              >
                ← Prev
              </button>
              <span className="text-xs font-bold text-slate-500">{page + 1} / {totalPages || 1}</span>
              <button
                disabled={page + 1 >= totalPages}
                onClick={() => setPage((p) => p + 1)}
                className="rounded-lg border px-3 py-1.5 text-xs font-bold text-slate-600 hover:bg-slate-100 disabled:opacity-40"
              >
                Next →
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// ─── Single approval row ──────────────────────────────────────────────────────

function ApprovalItem({
  claim, expanded, onToggle, onDecision,
}: {
  claim: ExpenseClaimSummary;
  expanded: boolean;
  onToggle: () => void;
  onDecision: () => void;
}) {
  const [comments, setComments]         = useState("");
  const [viewedDocIds, setViewedDocIds] = useState<Set<number>>(new Set());
  const [docWarning, setDocWarning]     = useState(false);
  const [lastAction, setLastAction]     = useState<"approved" | "rejected" | null>(null);
  const confirm = useConfirm();
  const toast = useToast();

  const approve = useApproveClaim();
  const reject  = useRejectClaim();
  const detail  = useClaim(expanded ? claim.id : undefined);

  const allDocs       = detail.data?.documents ?? [];
  const hasDocs       = allDocs.length > 0;
  const hasViewedADoc = viewedDocIds.size > 0;
  const isPending     = approve.isPending || reject.isPending;

  function markViewed(docId: number) {
    setViewedDocIds((prev) => new Set([...prev, docId]));
    setDocWarning(false);
  }

  async function handleApprove() {
    // If row not expanded yet, expand so the user can see and view docs
    if (!expanded) {
      onToggle();
      setDocWarning(true);
      return;
    }
    // Still loading detail
    if (detail.isLoading) return;
    // Must view at least one doc if any exist
    if (hasDocs && !hasViewedADoc) {
      setDocWarning(true);
      return;
    }
    if (!(await confirm("Approve this claim?"))) return;
    try {
      await approve.mutateAsync({ id: claim.id, payload: { comments: comments || null } });
      setLastAction("approved");
      onDecision();
    } catch { /* error surfaced via approve.isError */ }
  }

  async function handleReject() {
    if (!expanded) { onToggle(); return; } // expand for comments textarea
    if (!comments.trim()) { toast.error("Please provide a reason for rejection."); return; }
    if (!(await confirm("Reject this claim?"))) return;
    try {
      await reject.mutateAsync({ id: claim.id, payload: { comments } });
      setLastAction("rejected");
      onDecision();
    } catch { /* error surfaced via reject.isError */ }
  }

  return (
    <div>
      {/* ── Post-action flash ── */}
      {lastAction && (
        <div className={`flex items-center gap-2 px-4 py-2 text-xs font-bold ${
          lastAction === "approved" ? "bg-emerald-50 text-emerald-700" : "bg-red-50 text-red-700"
        }`}>
          {lastAction === "approved"
            ? <><CheckCircle2 className="h-3.5 w-3.5" /> Step approved — if the workflow has further steps, this claim will reappear below for the next approval.</>
            : <><XCircle className="h-3.5 w-3.5" /> Claim rejected.</>
          }
        </div>
      )}

      {/* ── Compact row ── */}
      <div className="flex items-center gap-3 px-4 py-2.5 transition-colors hover:bg-slate-50">

        {/* Type avatar */}
        <div className="flex h-7 w-7 shrink-0 items-center justify-center rounded-lg bg-amber-100 text-xs font-black text-amber-700">
          {claim.claimType.charAt(0)}
        </div>

        {/* Click area → expand */}
        <button
          type="button"
          onClick={onToggle}
          className="min-w-0 flex-1 text-left"
        >
          <div className="flex flex-wrap items-center gap-1.5">
            <span className="text-[11px] font-bold text-slate-400">{claim.claimNo}</span>
            <ClaimStatusBadge status={claim.status} />
            {claim.currentStepOrder != null && (
              <span className="rounded-full bg-amber-100 px-2 py-0.5 text-[10px] font-black text-amber-700">
                Step {claim.currentStepOrder}{claim.currentStepName ? ` · ${claim.currentStepName}` : ""}
              </span>
            )}
          </div>
          <div className="truncate text-sm font-bold text-slate-800">
            {claim.employeeName}
            <span className="ml-1.5 text-xs font-normal text-slate-500">
              — {typeLabel(claim.claimType)}
            </span>
          </div>
          <div className="text-[11px] text-slate-400">{fmtDate(claim.submittedAt)}</div>
        </button>

        {/* Amount */}
        <div className="shrink-0 text-right">
          <div className="text-sm font-black text-slate-800">{fmt(claim.totalAmount ?? 0)}</div>
        </div>

        {/* Inline action buttons */}
        <div className="flex shrink-0 items-center gap-1">
          <button
            type="button"
            onClick={handleApprove}
            disabled={isPending}
            title={expanded && hasDocs && !hasViewedADoc ? "View document first" : "Approve claim"}
            className="inline-flex items-center gap-1 rounded-lg bg-emerald-600 px-2.5 py-1.5 text-xs font-bold text-white hover:bg-emerald-700 disabled:opacity-50"
          >
            <CheckCircle2 className="h-3 w-3" />
            Approve
          </button>
          <button
            type="button"
            onClick={handleReject}
            disabled={isPending}
            title="Reject claim"
            className="inline-flex items-center gap-1 rounded-lg border border-red-200 bg-red-50 px-2.5 py-1.5 text-xs font-bold text-red-700 hover:bg-red-100 disabled:opacity-50"
          >
            <XCircle className="h-3 w-3" />
            Reject
          </button>
          <button
            type="button"
            onClick={onToggle}
            className="rounded-lg border p-1.5 text-slate-400 hover:bg-slate-100"
          >
            {expanded ? <ChevronUp className="h-3.5 w-3.5" /> : <ChevronDown className="h-3.5 w-3.5" />}
          </button>
        </div>
      </div>

      {/* ── Expanded detail panel ── */}
      {expanded && (
        <div className="border-t bg-amber-50/25 px-5 py-4">
          {detail.isLoading ? (
            <div className="flex items-center gap-2 text-sm text-slate-400">
              <Loader2 className="h-4 w-4 animate-spin" /> Loading claim details…
            </div>
          ) : !detail.data ? (
            <p className="text-sm text-slate-400">Failed to load details.</p>
          ) : (
            <div className="space-y-4">

              {/* Doc-view status banner */}
              {hasDocs && !hasViewedADoc && (
                <div
                  className={`flex items-start gap-2 rounded-xl border px-3 py-2.5 ${
                    docWarning
                      ? "border-red-200 bg-red-50"
                      : "border-amber-200 bg-amber-50"
                  }`}
                >
                  <Eye className={`mt-0.5 h-4 w-4 shrink-0 ${docWarning ? "text-red-500" : "text-amber-500"}`} />
                  <p className={`text-xs font-semibold ${docWarning ? "text-red-700" : "text-amber-700"}`}>
                    {docWarning
                      ? "You must view at least one attached document before approving."
                      : "Review the receipts below before approving."}
                  </p>
                </div>
              )}

              {hasDocs && hasViewedADoc && (
                <div className="flex items-center gap-2 rounded-xl border border-emerald-200 bg-emerald-50 px-3 py-2">
                  <CheckCircle2 className="h-3.5 w-3.5 shrink-0 text-emerald-600" />
                  <p className="text-xs font-semibold text-emerald-700">
                    Document reviewed · {viewedDocIds.size} of {allDocs.length} viewed
                  </p>
                </div>
              )}

              {/* Purpose */}
              {detail.data.purpose && (
                <div className="rounded-xl border bg-white px-4 py-3">
                  <span className="text-[10px] font-bold uppercase tracking-wide text-slate-400">Purpose</span>
                  <p className="mt-1 text-sm text-slate-700">{detail.data.purpose}</p>
                </div>
              )}

              {/* Expense lines + per-line documents */}
              <div>
                <h4 className="mb-2 text-[10px] font-black uppercase tracking-wide text-slate-400">
                  Expense Lines &amp; Receipts
                </h4>
                <div className="space-y-2">
                  {detail.data.lines.length === 0 ? (
                    <p className="text-xs text-slate-400">No lines added.</p>
                  ) : (() => {
                    const backendSupportsLineId = detail.data!.documents.some((d) => d.lineId != null);
                    return detail.data!.lines.map((l, idx) => {
                      const lineDocs = backendSupportsLineId
                        ? detail.data!.documents.filter((d) => d.lineId === l.id)
                        : idx === 0 ? detail.data!.documents : [];
                      return (
                        <div key={l.id} className="rounded-xl border bg-white p-3">
                          <div className="flex items-start gap-2">
                            <div className="flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-emerald-100 text-[10px] font-black text-emerald-700">
                              {idx + 1}
                            </div>
                            <div className="min-w-0 flex-1">
                              <div className="flex items-center justify-between gap-2">
                                <div>
                                  <div className="text-sm font-bold text-slate-700">{l.description}</div>
                                  <div className="text-[10px] text-slate-400">{fmtDate(l.expenseDate)}</div>
                                </div>
                                <span className="shrink-0 font-black text-emerald-700">{fmt(l.amount)}</span>
                              </div>

                              {/* Per-line doc with view button */}
                              {lineDocs.length === 0 ? (
                                <p className="mt-1.5 text-[10px] italic text-amber-500">No document attached</p>
                              ) : (
                                <div className="mt-2 space-y-1">
                                  {lineDocs.map((d) => (
                                    <div
                                      key={d.id}
                                      className={`flex items-center gap-2 rounded-lg border px-2 py-1.5 transition-colors ${
                                        viewedDocIds.has(d.id)
                                          ? "border-emerald-200 bg-emerald-50"
                                          : "border-slate-200 bg-slate-50"
                                      }`}
                                    >
                                      <FileText
                                        className={`h-3 w-3 shrink-0 ${
                                          viewedDocIds.has(d.id) ? "text-emerald-500" : "text-slate-400"
                                        }`}
                                      />
                                      <span className="min-w-0 flex-1 truncate text-[11px] font-semibold text-slate-700">
                                        {d.originalFileName}
                                      </span>
                                      {viewedDocIds.has(d.id) && (
                                        <span className="shrink-0 text-[10px] font-bold text-emerald-600">✓ Viewed</span>
                                      )}
                                      <ClaimDocumentViewer
                                        claimId={claim.id}
                                        doc={d}
                                        compact
                                        onOpen={() => markViewed(d.id)}
                                      />
                                    </div>
                                  ))}
                                </div>
                              )}
                            </div>
                          </div>
                        </div>
                      );
                    });
                  })()}

                  {detail.data.lines.length > 0 && (
                    <div className="flex justify-end pt-1 text-sm font-black text-slate-700">
                      Total: {fmt(detail.data.lines.reduce((s, l) => s + l.amount, 0))}
                    </div>
                  )}
                </div>
              </div>

              {/* Additional docs (claim-level, only when backend supports lineId) */}
              {detail.data.documents.some((d) => d.lineId != null) &&
                detail.data.documents.filter((d) => !d.lineId).length > 0 && (
                <div>
                  <h4 className="mb-2 text-[10px] font-black uppercase tracking-wide text-slate-400">
                    Additional Documents
                  </h4>
                  <div className="space-y-1.5">
                    {detail.data.documents.filter((d) => !d.lineId).map((d) => (
                      <div
                        key={d.id}
                        className={`flex items-center gap-2 rounded-xl border px-3 py-2 transition-colors ${
                          viewedDocIds.has(d.id) ? "border-emerald-200 bg-emerald-50" : "border-slate-200 bg-white"
                        }`}
                      >
                        <FileText className={`h-4 w-4 shrink-0 ${viewedDocIds.has(d.id) ? "text-emerald-500" : "text-slate-400"}`} />
                        <span className="min-w-0 flex-1 truncate text-xs font-semibold text-slate-700">
                          {d.originalFileName}
                        </span>
                        {viewedDocIds.has(d.id) && (
                          <span className="shrink-0 text-[10px] font-bold text-emerald-600">✓ Viewed</span>
                        )}
                        <ClaimDocumentViewer
                          claimId={claim.id}
                          doc={d}
                          compact
                          onOpen={() => markViewed(d.id)}
                        />
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Decision panel */}
              <div className="rounded-2xl border border-slate-200 bg-white p-4">
                <div className="mb-3 flex items-center justify-between">
                  <h4 className="text-sm font-black text-slate-700">Decision</h4>
                  {claim.currentStepOrder != null && (
                    <span className="rounded-full bg-amber-100 px-2.5 py-1 text-[11px] font-black text-amber-700">
                      Step {claim.currentStepOrder}{claim.currentStepName ? ` — ${claim.currentStepName}` : ""}
                    </span>
                  )}
                </div>
                <textarea
                  rows={2}
                  value={comments}
                  onChange={(e) => setComments(e.target.value)}
                  placeholder="Add comments (required for rejection)…"
                  className="w-full resize-none rounded-xl border bg-slate-50 px-3 py-2 text-sm placeholder:text-slate-300 focus:outline-none focus:ring-2 focus:ring-emerald-500"
                />
                <div className="mt-3 flex flex-wrap items-center gap-2">
                  <button
                    type="button"
                    onClick={handleApprove}
                    disabled={isPending || (hasDocs && !hasViewedADoc)}
                    title={hasDocs && !hasViewedADoc ? "View attached document first" : "Approve this claim"}
                    className="inline-flex items-center gap-2 rounded-xl bg-emerald-700 px-4 py-2 text-sm font-bold text-white hover:bg-emerald-800 disabled:cursor-not-allowed disabled:opacity-50"
                  >
                    <CheckCircle2 className="h-4 w-4" />
                    {approve.isPending ? "Approving…" : "Approve"}
                  </button>
                  <button
                    type="button"
                    onClick={handleReject}
                    disabled={isPending}
                    className="inline-flex items-center gap-2 rounded-xl border border-red-200 bg-red-50 px-4 py-2 text-sm font-bold text-red-700 hover:bg-red-100 disabled:opacity-50"
                  >
                    <XCircle className="h-4 w-4" />
                    {reject.isPending ? "Rejecting…" : "Reject"}
                  </button>
                  {hasDocs && !hasViewedADoc && (
                    <span className="text-xs font-semibold text-amber-600">
                      ↑ View a receipt above to enable approval
                    </span>
                  )}
                  <Link
                    to={`/claims/${claim.id}`}
                    className="ml-auto text-xs font-bold text-slate-500 hover:underline"
                  >
                    Full view →
                  </Link>
                </div>
                {(approve.isError || reject.isError) && (
                  <p className="mt-2 text-xs font-semibold text-red-600">
                    Action failed. Please try again.
                  </p>
                )}
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
